import { useSelector } from "react-redux";
import { Box, Typography, alpha } from "@mui/material";
import { ScheduleOutlined } from "@mui/icons-material";
import { RootState } from "@store/store";
import useLoanStatusPolling from "@hooks/useLoanStatusPolling";
import { STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";

/**
 * Sala de espera genérica: la solicitud ya se envió y se está calculando la oferta. Es la misma pantalla
 * que `WaitingRoomConcluida` (marketplace) con textos sin marketplace ni financieras aliadas; cada empresa
 * puede tener la suya en `waitingRoomPorEmpresa`. Sondea la solicitud hasta que el backend decide
 * (`useLoanStatusPolling`).
 */
export const WaitingRoom = () => {
  // PM saluda a la empresa (o a la persona si no hay empresa); PFAE y PF, a la persona. En PFAE `company.name`
  // es el nombre comercial (en Mercader, el del consultorio; QA ANA-05).
  const personName = useSelector((state: RootState) => state.register.personal_data.name)?.trim();
  const companyName = useSelector((state: RootState) => state.register.company.name)?.trim();
  const taxSystemType = useSelector((state: RootState) => state.register.application.tax_system_type);
  const greetingName = taxSystemType === TAX_SYSTEM_TYPE.PM.code ? companyName || personName : personName;
  useLoanStatusPolling(STATUS_CATALOG.waiting_room.code, "WaitingRoom");

  return (
    <Box sx={{ maxWidth: 860, mx: "auto" }}>
      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "24px",
          boxShadow: "0 18px 50px rgba(6, 44, 78, 0.10)",
          p: { xs: "40px 24px", sm: "56px 64px" },
          textAlign: "center",
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "8px", mb: "22px" }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "success.main" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 13, letterSpacing: "0.14em", color: "success.main" }}>
            SOLICITUD CONCLUIDA
          </Typography>
        </Box>

        <Typography
          component="h1"
          sx={{ fontWeight: 600, fontSize: { xs: 30, sm: 38 }, lineHeight: 1.18, color: "primary.dark", m: "0 0 20px" }}
        >
          ¡Felicidades{greetingName ? `, ${greetingName}` : ""}!
          <br />
          Concluiste tu solicitud
        </Typography>

        <Typography
          sx={{ fontSize: 17, lineHeight: 1.6, color: "tokens.textSecondary", maxWidth: 620, m: "0 auto 8px" }}
        >
          Recibimos tu información y estamos analizando tu perfil para preparar tu oferta.
        </Typography>

        {/*
         * Orbe de espera: dos anillos fijos, un barrido cónico que gira (`orbit`) y un halo que late hacia
         * afuera (`pulse`) detrás del núcleo sólido. Igual que en `WaitingRoomConcluida`.
         */}
        <Box
          sx={(theme) => ({
            position: "relative",
            width: 200,
            height: 200,
            m: "28px auto 24px",
            "@keyframes orbit": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
            "@keyframes pulse": {
              "0%": { transform: "translate(-50%, -50%) scale(.7)", opacity: 0.55 },
              "70%": { opacity: 0 },
              "100%": { transform: "translate(-50%, -50%) scale(1.35)", opacity: 0 },
            },
            "& > *": { position: "absolute", borderRadius: "50%" },
            "& .orb-centered": { top: "50%", left: "50%", transform: "translate(-50%, -50%)" },
            "& .orb-ring-outer": { inset: 0, border: `1.5px solid ${alpha(theme.palette.primary.main, 0.28)}` },
            "& .orb-ring-inner": { inset: "34px", border: `1px solid ${alpha(theme.palette.primary.main, 0.19)}` },
          })}
        >
          <Box className="orb-ring-outer" />
          <Box className="orb-ring-inner" />
          <Box
            sx={(theme) => ({
              inset: 0,
              animation: "orbit 2.2s linear infinite",
              background: `conic-gradient(from 0deg, ${alpha(theme.palette.primary.main, 0)} 0deg, ${alpha(
                theme.palette.primary.main,
                0.32,
              )} 160deg, ${alpha(theme.palette.primary.main, 0)} 161deg)`,
            })}
          />
          <Box
            className="orb-centered"
            sx={(theme) => ({
              width: 132,
              height: 132,
              animation: "pulse 1.6s ease-out infinite",
              bgcolor: alpha(theme.palette.primary.main, 0.16),
            })}
          />
          <Box
            className="orb-centered"
            sx={(theme) => ({
              width: 72,
              height: 72,
              bgcolor: "primary.main",
              boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.4)}`,
            })}
          />
        </Box>

        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            gap: "14px",
            justifyContent: "center",
            maxWidth: 560,
            m: "0 auto",
            p: "18px 22px",
            bgcolor: alpha(theme.palette.primary.main, 0.09),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
            borderRadius: "14px",
          })}
        >
          <ScheduleOutlined sx={{ fontSize: 22, color: "primary.main", flexShrink: 0 }} />
          <Typography sx={{ fontSize: 15.5, lineHeight: 1.45, color: "primary.dark", textAlign: "left" }}>
            En cuanto tu <strong>oferta</strong> esté lista, pasarás a verla aquí mismo.
          </Typography>
        </Box>

        <Typography
          sx={{ fontSize: 14.5, lineHeight: 1.55, color: "tokens.textTertiary", maxWidth: 520, m: "24px auto 0" }}
        >
          Te avisaremos aquí mismo y vía WhatsApp. Puedes cerrar esta ventana con tranquilidad.
        </Typography>
      </Box>
    </Box>
  );
};
